import { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EVENTS, Joyride, STATUS } from 'react-joyride';
import type { EventHandler, Step } from 'react-joyride';
import { TourTooltip } from './TourTooltip';
import { filterValidSteps, useJoyrideOptions } from './tourUtils';

interface StoryTimeTourProps {
  open: boolean;
  onComplete: () => void;
  onSkip: () => void;
}

/** Delay (ms) after `open` flips true before resolving anchors — the StoryTime
 * form renders synchronously, so a short delay just lets the page settle
 * before anchors are queried (mirrors NewProjectTour's pattern). */
const ANCHOR_MOUNT_DELAY = 100;

export const StoryTimeTour = ({ open, onComplete, onSkip }: StoryTimeTourProps) => {
  const { t } = useTranslation('common');
  const joyrideOptions = useJoyrideOptions();
  const [steps, setSteps] = useState<Step[]>([]);

  useEffect(() => {
    if (!open) {
      setSteps([]);
      return;
    }

    const timer = window.setTimeout(() => {
      const candidates: Step[] = [
        {
          target: 'body',
          placement: 'center',
          title: t('storyTimeTour.welcomeTitle', 'Welcome to StoryTime'),
          content: t(
            'storyTimeTour.welcomeDescription',
            "StoryTime uses AI to generate personalized reading practice — short stories written with the vocabulary you've collected while studying."
          ),
        },
        {
          target: 'body',
          placement: 'center',
          title: t('storyTimeTour.vocabTitle', 'You need 50 vocabulary words first'),
          content: (
            <>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'storyTimeTour.vocabDescription1',
                  'Stories are generated from your own vocabulary, so you need at least 50 unique words saved in your target language before generating.'
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                {t(
                  'storyTimeTour.vocabDescription2',
                  "If you see 'Build more vocabulary before generating stories', keep adding words from podcast and video transcripts — every word you save grows your profile."
                )}
              </Typography>
            </>
          ),
        },
        {
          target: '[data-tour="storytime-topic"]',
          title: t('storyTimeTour.topicTitle', 'Pick a Topic'),
          content: t(
            'storyTimeTour.topicDescription',
            'Tell the AI what your story should be about — anything from "space exploration" to "a cat who opens a bakery".'
          ),
        },
        {
          target: '[data-tour="storytime-length"]',
          title: t('storyTimeTour.lengthTitle', 'Choose a Length'),
          content: t(
            'storyTimeTour.lengthDescription',
            'Short, medium, or long — pick how much reading you want to do.'
          ),
        },
        {
          target: '[data-tour="storytime-language"]',
          title: t('storyTimeTour.languageTitle', 'Study Language'),
          content: t(
            'storyTimeTour.languageDescription',
            'Choose which of your study languages the story is written in. Your vocabulary in that language shapes the story.'
          ),
        },
        {
          target: '[data-tour="storytime-generate"]',
          title: t('storyTimeTour.generateTitle', 'Generate Your Story'),
          content: t(
            'storyTimeTour.generateDescription',
            "When you're ready, hit Generate. Your story appears below, and past stories are saved in Story History."
          ),
        },
      ];

      // The language selector only renders for multi-language users —
      // filterValidSteps drops that step gracefully when absent.
      setSteps(filterValidSteps(candidates));
    }, ANCHOR_MOUNT_DELAY);

    return () => window.clearTimeout(timer);
  }, [open, t]);

  const handleEvent: EventHandler = (data) => {
    if (data.type !== EVENTS.TOUR_END) {
      return;
    }
    if (data.status === STATUS.FINISHED) {
      // Persists storytime_tour_completed via useStoryTimeTour
      onComplete();
    } else if (data.status === STATUS.SKIPPED) {
      // Persists storytime_tour_skipped (tour re-offers next visit)
      onSkip();
    }
  };

  // If no anchors survived filtering, render nothing without completing —
  // the tour retries on next mount when the anchors exist.
  if (!open || steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      run
      continuous
      steps={steps}
      tooltipComponent={TourTooltip}
      onEvent={handleEvent}
      options={{
        ...joyrideOptions,
        blockTargetInteraction: true,
        closeButtonAction: 'skip',
        overlayClickAction: false,
      }}
    />
  );
};
